import React, { ChangeEvent } from "react";

import Box from "veneer/Box";
import Stack from "veneer/Stack";
import styled from "veneer/styled";
import TextInput from "veneer/TextInput";
import { OverflowTooltipTypography } from "veneer/Typography";

interface TargetDetails {
  duration?: number;
  icon?: string;
  name?: string;
  type?: string;
}

interface TargetCardProps {
  details?: TargetDetails;
  duration?: number;
  id?: number | string;
  isSelectable?: boolean;
  onChangeDuration?: (duration: number) => void;
  onClick?: () => void;
  selectedId?: number | string;
}

const TargetCardRoot = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "$inactive" && prop !== "$noHover" && prop !== "$selected",
})<{
  $inactive: boolean;
  $noHover: boolean;
  $selected: boolean;
}>(({ $inactive, $noHover, $selected }) => ({
  border: "2px solid #ccc",
  float: "none",
  textAlign: "center",
  "&:hover": {
    borderColor: $noHover ? "#ccc" : "#3c8dbc",
    cursor: "pointer",
  },
  "& .user-content-overlay": {
    bottom: 0,
    position: "relative",
    width: "100%",
  },
  ...($inactive ? { opacity: 0.25 } : {}),
  ...($selected ? { borderColor: "#3c8dbc" } : {}),
  borderWidth: 1,
  marginBottom: 10,
  width: 110,
}));

const TargetCardHeader = styled(Stack)({
  paddingTop: 10,
  textAlign: "center",
  "& i": {
    color: "#ccc",
  },
});

const TargetCardIconImage = styled("img")({
  display: "block",
});

const TargetCardIconVideo = styled("video")({
  display: "block",
});

const TargetCardDurationInput = styled(TextInput)({
  borderBottomWidth: 0,
  borderLeftWidth: 0,
  fontSize: 12,
  padding: 0,
  textAlign: "center",
  "&:disabled": {
    backgroundColor: "initial",
  },
  "&:focus": {
    borderBottomWidth: 1,
    borderLeftWidth: 1,
  },
});

const TargetCardDurationOverlay = styled(Box)({
  bottom: 0,
  position: "relative",
  width: "100%",
});

export default function TargetCard({
  details = {},
  duration,
  id,
  isSelectable = true,
  onChangeDuration,
  onClick,
  selectedId,
}: TargetCardProps) {
  const renderIcon = (targetDetails: TargetDetails) => {
    const { type } = targetDetails;

    if (!type) {
      return null;
    }

    if (type.startsWith("image") || type === "application") {
      return (
        <TargetCardIconImage className="app-icon" src={targetDetails.icon} />
      );
    }

    if (type.startsWith("video")) {
      return (
        <TargetCardIconVideo className="app-icon" controls preload="none">
          <source src={targetDetails.icon} />
        </TargetCardIconVideo>
      );
    }

    return null;
  };

  const isTargetInactive = () => {
    return Boolean(selectedId && String(selectedId) !== String(id));
  };

  const isTargetSelected = () => {
    return Boolean(
      isSelectable && selectedId && String(selectedId) === String(id),
    );
  };

  const handleChangeDuration = (event: ChangeEvent<HTMLInputElement>) => {
    const duration = Number.parseInt(event.currentTarget.value, 10) * 1000;
    onChangeDuration?.(duration);
  };

  const durationSeconds = (duration || details.duration || 0) / 1000;

  return (
    <TargetCardRoot
      $inactive={isTargetInactive()}
      $noHover={!isSelectable}
      $selected={isTargetSelected()}
    >
      <TargetCardHeader
        key={`app-icon-${id}`}
        onClick={onClick}
        p={1}
        spacing={1}
      >
        {renderIcon(details)}
        <OverflowTooltipTypography variant="body2">
          {details.name}
        </OverflowTooltipTypography>
        {details.type && !details.type.startsWith("video") && (
          <TargetCardDurationOverlay>
            <TargetCardDurationInput
              disabled={!isSelectable}
              endAdornment="sec"
              onChange={(value) =>
                handleChangeDuration({
                  currentTarget: { value },
                } as ChangeEvent<HTMLInputElement>)
              }
              value={String(durationSeconds)}
            />
          </TargetCardDurationOverlay>
        )}
      </TargetCardHeader>
    </TargetCardRoot>
  );
}
